import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { CheckCircle2, ChevronDown, Loader2, X } from 'lucide-react'
import { Dialog, DialogContent } from '../ui/Dialog'
import { useFeedbackContext } from './FeedbackContext'
import { submitFeedback, type FeedbackPayload } from '../../services/feedbackService'
import { getRouteTitle } from '../layout/routeTitles'
import type { RootState } from '../../store'
import { BRAND } from '../../config/brand'

type Category = FeedbackPayload['category']
type Severity = NonNullable<FeedbackPayload['severity']>

const categories: { value: Category; label: string }[] = [
  { value: 'bug', label: 'Bug / Broke' },
  { value: 'expectation_mismatch', label: "Didn't match expectations" },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'other', label: 'Other' },
]

const severities: { value: Severity; label: string }[] = [
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'major', label: 'Major' },
]

interface FeedbackModalProps {
  open: boolean
  onClose: () => void
}

export function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  const { pathname } = useLocation()
  const { user } = useSelector((s: RootState) => s.auth)
  const { pageContext } = useFeedbackContext()

  const [category, setCategory] = useState<Category | null>(null)
  const [expected, setExpected] = useState('')
  const [actual, setActual] = useState('')
  const [improvement, setImprovement] = useState('')
  const [severity, setSeverity] = useState<Severity | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [contextExpanded, setContextExpanded] = useState(false)

  const needsActual = category === 'bug' || category === 'expectation_mismatch'
  const canSubmit = category && expected.trim() && (!needsActual || actual.trim())

  const resetForm = () => {
    setCategory(null)
    setExpected('')
    setActual('')
    setImprovement('')
    setSeverity(null)
    setError(null)
    setSubmitted(false)
  }

  const handleClose = () => {
    onClose()
    setTimeout(resetForm, 300)
  }

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return
    setSubmitting(true)
    setError(null)

    const payload: FeedbackPayload = {
      category: category!,
      expected: expected.trim(),
      actual: actual.trim() || undefined,
      improvement: improvement.trim() || undefined,
      severity: severity || undefined,
      context: {
        page_route: pathname,
        page_title: getRouteTitle(pathname),
        user_agent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        is_mobile: window.innerWidth < 768,
        timestamp: new Date().toISOString(),
        page_context: pageContext
          ? { ...pageContext, ...(pageContext.extra || {}) }
          : undefined,
      },
    }

    try {
      await submitFeedback(payload)
      setSubmitted(true)
      setTimeout(handleClose, 1500)
    } catch {
      setError('Failed to submit. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="p-0">
        {submitted ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 px-6">
            <div className="w-14 h-14 rounded-full bg-ok/10 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-ok" />
            </div>
            <p className="text-lg font-semibold text-primary">Thank you!</p>
            <p className="text-sm text-muted">Your feedback helps us improve {BRAND.assistant}.</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h2 className="text-lg font-semibold text-primary">Share Feedback</h2>
              <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-surface-alt text-muted">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 pb-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Category */}
              <div>
                <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-2">Category</label>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map(c => (
                    <button
                      key={c.value}
                      onClick={() => setCategory(c.value)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                        category === c.value
                          ? 'bg-brand-subtle border-brand-subtle text-brand'
                          : 'bg-surface-alt border-soft text-secondary hover:border-brand-subtle'
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Expected */}
              <div>
                <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-1.5">
                  What did you expect? <span className="text-bad">*</span>
                </label>
                <textarea
                  value={expected}
                  onChange={e => setExpected(e.target.value)}
                  placeholder="I expected..."
                  rows={2}
                  className="w-full rounded-lg border border-soft bg-surface-alt px-3 py-2 text-sm text-primary placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                />
              </div>

              {/* Actual */}
              {needsActual && (
                <div>
                  <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-1.5">
                    What actually happened? <span className="text-bad">*</span>
                  </label>
                  <textarea
                    value={actual}
                    onChange={e => setActual(e.target.value)}
                    placeholder="Instead, what happened was..."
                    rows={2}
                    className="w-full rounded-lg border border-soft bg-surface-alt px-3 py-2 text-sm text-primary placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                  />
                </div>
              )}

              {/* Improvement */}
              <div>
                <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-1.5">
                  How could it be improved? <span className="text-muted text-app-micro">(optional)</span>
                </label>
                <textarea
                  value={improvement}
                  onChange={e => setImprovement(e.target.value)}
                  placeholder="It would be better if..."
                  rows={2}
                  className="w-full rounded-lg border border-soft bg-surface-alt px-3 py-2 text-sm text-primary placeholder-muted focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                />
              </div>

              {/* Severity */}
              <div>
                <label className="block text-xs font-semibold text-muted uppercase tracking-wider mb-2">
                  Severity <span className="text-muted text-app-micro">(optional)</span>
                </label>
                <div className="flex gap-2">
                  {severities.map(s => (
                    <button
                      key={s.value}
                      onClick={() => setSeverity(severity === s.value ? null : s.value)}
                      className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        severity === s.value
                          ? s.value === 'major'
                            ? 'bg-bad/10 border-bad/30 text-bad'
                            : s.value === 'moderate'
                              ? 'bg-warn/10 border-warn/30 text-warn'
                              : 'bg-brand-subtle border-brand-subtle text-brand'
                          : 'bg-surface-alt border-soft text-muted hover:border-soft'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Auto-context preview */}
              <div>
                <button
                  onClick={() => setContextExpanded(!contextExpanded)}
                  className="flex items-center gap-1.5 text-app-micro text-muted font-medium uppercase tracking-wider hover:text-secondary transition-colors"
                >
                  <ChevronDown className={`w-3 h-3 transition-transform ${contextExpanded ? 'rotate-180' : ''}`} />
                  Context attached
                </button>
                {contextExpanded && (
                  <div className="mt-1.5 p-2 rounded-lg bg-surface-alt border border-soft/50 text-app-micro text-muted font-mono space-y-0.5">
                    <div>page: {pathname}</div>
                    <div>title: {getRouteTitle(pathname)}</div>
                    <div>user: {user?.email || user?.username || 'unknown'}</div>
                    <div>viewport: {typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'N/A'}</div>
                    {pageContext && (
                      <>
                        <div>page_type: {pageContext.pageType}</div>
                        {pageContext.entityType && <div>entity: {pageContext.entityType}{pageContext.entityId ? ` #${pageContext.entityId}` : ''}</div>}
                        {pageContext.extra && Object.entries(pageContext.extra).map(([k, v]) => (
                          v != null && <div key={k}>{k}: {String(v)}</div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Error */}
              {error && (
                <p className="text-xs text-bad font-medium">{error}</p>
              )}

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  canSubmit && !submitting
                    ? 'bg-brand hover:bg-brand-hover text-on-brand shadow-sm'
                    : 'bg-soft text-muted cursor-not-allowed'
                }`}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
